import React, {
    useEffect, Suspense, useState
} from 'react';
import FullScreenLoader from '@shared/FullScreenLoader/FullScreenLoader';
import {
    BrowserRouter as Router,
    Switch,
} from 'react-router-dom';

// Okta
import { Security } from '@okta/okta-react';
import { OktaAuth } from '@okta/okta-auth-js';
import { getOktaIssuerUrl } from '@helpers/getDashboardRedirectUrl';
import { Helmet } from 'react-helmet';
import { I18n } from 'react-redux-i18n';
import { useLocalStorageState } from '@helpers/customHooks/useLocalStorageState';
import { daysUntilScheduledMaintenance } from '@helpers/scheduledMaintenance';

import TopBarComponent from '@landing/TopBar/TopBar';
import { TrackRoute } from '@shared/Routing/TrackRoute';
import PageFooter from '@landing/PageFooter/PageFooter';
import { useTranslation } from '@helpers/customHooks/useTranslation';
import { getLanguageCode } from '@helpers/languages';
import { Notification } from '@customTypes/ItemTypes';
import config from '@oktaConfig/oktaConfig';
import { SHOW_MFA_NOTIFICATION_BANNER } from '@helpers/constants';
import { setIsMobile, setIsScreenWidthUnder600Pixel, setIsScreenWidthUnder768Pixel } from '@redux/slices/appSlice';
import { useAppDispatch, useAppSelector } from '@redux/reduxHooks';
import { RootState } from '@redux/store';
import getBaseUrl from '@helpers/getBaseUrl';
import NotificationsWrapper from './landing/NotificationsWrapper/NotificationsWrapper';
import routes from '../routes';
import appleTouchIcon from '../apple-touch-icon.png';
import favicon from '../favicon.ico';
import { RoutesAuthCheck } from './RoutesAuthCheck';

const Main: React.FC = () => {
    const search = window.location.search;
    const urlParams = new URLSearchParams(search);
    const [legalTermIdFromEmail, setLegalTermIdFromEmail] = useLocalStorageState('LEGAL_TERM_ID');
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [owlBannerFlag, setOwlBannerFlag] = useLocalStorageState('SHOW_OWL_RESPONSE_BANNER');
    const mfaBannerFlag = SHOW_MFA_NOTIFICATION_BANNER;
    const languagePreference = useAppSelector((state: RootState) => state.app.profileDetails.languagePreference) || getLanguageCode();
    useTranslation(languagePreference || getLanguageCode());
    useEffect(() => {
        if (owlBannerFlag) {
            setNotifications((notifications) => [...notifications.filter((n) => n.key !== 'Owl Error Notification'), {
                type: 'error',
                message: I18n.t('owl-banner-error'),
                key: 'Owl Error Notification',
                cta: true,
            }]);
        }

        if (mfaBannerFlag) {
            setNotifications((notifications) => [...notifications.filter((n) => n.key !== 'MFA Notification'), {
                type: 'warning',
                message: I18n.t('mfa-notification-banner'),
                key: 'MFA Notification',
                iconType: 'triangle',
                closeButton: true,
                cta: true,
                ctaLearn: 'https://www.dnb.com/content/dam/DNB/pdf/MFA_Rollout_2024.pdf'
            }]);
        }

        if (languagePreference) {
            const { daysLeft, message } = daysUntilScheduledMaintenance(languagePreference);
            if (daysLeft >= 0 && daysLeft <= 7) {
                setNotifications((notifications) => [...notifications.filter((n) => n.key !== 'Scheduled Maintenance Notification'), {
                    type: 'info',
                    message,
                    key: 'Scheduled Maintenance Notification',
                    bold: true,
                    cta: true
                }]);
            }
        }
    }, [languagePreference]);

    useEffect(() => {
        const legalTermId = urlParams.get('termId');
        legalTermId && setLegalTermIdFromEmail(legalTermId);
    }, []);

    const dispatch = useAppDispatch();
    const bindDispatch = () => {
        dispatch(setIsScreenWidthUnder600Pixel(window.innerWidth <= 600));
        dispatch(setIsScreenWidthUnder768Pixel(window.innerWidth <= 767));
        dispatch(setIsMobile(window.innerWidth <= 1023));
    };

    getOktaIssuerUrl();

    useEffect(() => {
        window.addEventListener('resize', bindDispatch);
        return () => window.removeEventListener('resize', bindDispatch);
    }, []);

    const oktaAuth = new OktaAuth(config.oidc);

    const customAuthHandler = () => {
        // Redirect to the /login page if any error occur for the AusthSDK
        window.location.replace(`${getBaseUrl()}/login`);
    };
    const restoreOriginalUri = async () => {
        window.location.replace(getBaseUrl());
    };
    /**
     * Page Title based on the Router Config title attribute
     * @param title
     */
    const PageTitle = ({ title }: { title: string }): JSX.Element => (
        <>
            <Helmet>
                <title>{`${I18n.t('dnb-title')} - ${I18n.t(title)}`}</title>
                <link rel="icon" type="image/png" href={favicon} sizes="16x16" />
                <link
                    rel="apple-touch-icon"
                    href={appleTouchIcon}
                    sizes="180x180"
                />
            </Helmet>
        </>
    );
    return (
        <Suspense fallback={<FullScreenLoader isLoading />}>
            <Router>
                <Security
                    oktaAuth={oktaAuth}
                    onAuthRequired={customAuthHandler}
                    restoreOriginalUri={restoreOriginalUri}
                >
                    <Switch>
                        {routes.map((route) => (
                            route.isSecure
                                ? (
                                    <TrackRoute
                                        secure
                                        key={route.pageId}
                                        path={route.path}
                                        render={() => (
                                            <>
                                                <PageTitle title={route.title} />

                                                {route.showCPLHeader && (
                                                    <div style={{
                                                        position: 'sticky', top: 0, width: '100%', zIndex: 35, gridArea: 'header'
                                                    }}
                                                    >
                                                        <NotificationsWrapper
                                                            otherNotifications={notifications}
                                                            pageId={route.pageId}
                                                        />
                                                        <TopBarComponent />
                                                    </div>
                                                )}
                                                <RoutesAuthCheck
                                                    redirectTo={route.path}
                                                    isSecure={route.isSecure}
                                                >
                                                    {route.component}
                                                </RoutesAuthCheck>
                                                {route.showFooter && <PageFooter />}
                                            </>
                                        )}
                                    />
                                )
                                : (
                                    <TrackRoute
                                        key={route.pageId}
                                        path={route.path}
                                        render={() => (
                                            <>
                                                <PageTitle title={route.title} />
                                                <RoutesAuthCheck
                                                    redirectTo={route.path}
                                                    isSecure={false}
                                                >
                                                    {route.component}
                                                </RoutesAuthCheck>
                                                {route.showFooter && <PageFooter />}
                                            </>
                                        )}
                                    />
                                )
                        ))}
                    </Switch>
                </Security>
            </Router>
        </Suspense>
    );
};

export default Main;
